import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Stack
} from '@mui/material'
import { keyframes } from '@mui/system'
import RefreshIcon from '@mui/icons-material/Refresh'

// Animações
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`

export type RetryType = 'file' | 'transcription'

interface ConfirmRetryDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  retryType: RetryType
  fileName?: string
  isRetrying?: boolean
}

/**
 * Modal de confirmação para reprocessamento de arquivos
 * Similar ao ConfirmDeleteDialog, mas para ação de retry
 */
export default function ConfirmRetryDialog({
  open,
  onClose,
  onConfirm,
  retryType,
  fileName,
  isRetrying = false
}: ConfirmRetryDialogProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleConfirm = () => {
    if (!isRetrying) {
      onConfirm()
    }
  }

  const handleClose = () => {
    if (!isRetrying) {
      onClose()
    }
  }

  // Textos baseados no tipo de retry
  const title = retryType === 'file' 
    ? 'Reprocessar arquivo completo?'
    : 'Reprocessar transcrição?'

  const description = retryType === 'file'
    ? 'O arquivo será reenviado para processamento completo. A transcrição e todas as análises serão refeitas.'
    : 'A transcrição existente será reanalisada. O arquivo de áudio não será reprocessado.'

  const warningText = 'Esta ação solicitará um novo processamento e pode levar alguns minutos.'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          animation: `${fadeIn} 0.3s ease-out`,
          borderRadius: isMobile ? 0 : 2
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'info.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: isRetrying ? `${shake} 0.5s ease-in-out infinite` : 'none'
          }}
        >
          {isRetrying ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            <RefreshIcon sx={{ color: 'white', fontSize: 24 }} />
          )}
        </Box>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {fileName && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                borderLeft: '4px solid',
                borderColor: 'info.main'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {fileName}
              </Typography>
            </Box>
          )}

          <Typography variant="body1" color="text.primary">
            {description}
          </Typography>

          <Box
            sx={{
              p: 1.5,
              bgcolor: 'info.lighter',
              borderRadius: 1,
              borderLeft: '4px solid',
              borderColor: 'info.main'
            }}
          >
            <Typography variant="body2" color="info.dark">
              💡 {warningText}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button 
          onClick={handleClose}
          disabled={isRetrying}
          sx={{ minWidth: 100 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="info"
          disabled={isRetrying}
          startIcon={isRetrying ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{ 
            minWidth: 120,
            '&:hover': {
              transform: 'scale(1.02)'
            },
            transition: 'transform 0.2s'
          }}
        >
          {isRetrying ? 'Processando...' : 'Reprocessar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
